import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, finalize, map, shareReplay, switchMap, tap, throwError } from 'rxjs';

import { SessionService } from '../services/session.service';
import { AuthService } from 'src/app/features/auth/services/auth.service';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { BASE_URL } from 'src/environments/environment';

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenRefresh = inject(TokenRefreshService);
  const isApiRequest = req.url.startsWith(BASE_URL);
  const request = isApiRequest ? attachAccessToken(req) : req;

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!shouldAttemptRefresh(error, req, isApiRequest)) {
        if (error?.status === 401 && isApiRequest && !isAuthRequest(req.url)) {
          tokenRefresh.logout();
        }
        return throwError(() => error);
      }

      return tokenRefresh.refreshAccessToken().pipe(
        switchMap((accessToken) => next(attachAccessToken(req, accessToken))),
        catchError((refreshError) => {
          tokenRefresh.logout();
          return throwError(() => refreshError);
        })
      );
    })
  );
};

/** The refresh and sign-out state every intercepted request shares. */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(SessionService);
  private loggingOut = false;
  private refreshRequest$: Observable<string> | null = null;

  refreshAccessToken(): Observable<string> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const currentUser = LocalStorageService.get('currentUser');
    if (!currentUser?.refresh_token) {
      return throwError(() => new Error('Missing refresh token'));
    }

    this.refreshRequest$ = this.authService.refreshAccessToken(currentUser.refresh_token).pipe(
      // The retailer endpoint returns a single object; ``data`` is the list form.
      map((response) => response.object ?? response.data?.[0]),
      tap((tokens) => {
        if (!tokens?.access_token || !tokens?.refresh_token) {
          throw new Error('Incomplete refresh response');
        }

        // Store both halves: the server rotates the refresh token on every use
        // and treats a replay as theft, so keeping the old value would revoke
        // the session on the next refresh.
        LocalStorageService.put('currentUser', {
          ...currentUser,
          token: tokens.access_token,
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          token_type: tokens.token_type,
          expires_in: tokens.expires_in,
        });
      }),
      map((tokens) => tokens!.access_token),
      finalize(() => {
        this.refreshRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshRequest$;
  }

  /**
   * Forced sign-out after an unrecoverable 401.
   *
   * Local-only: the refresh token was already rejected, or never existed.
   */
  logout(): void {
    if (this.loggingOut) {
      return;
    }

    this.loggingOut = true;
    this.sessionService.clearLocalSession().then(() => {
      this.loggingOut = false;
    });
  }
}

function attachAccessToken(request: HttpRequest<unknown>, accessToken?: string): HttpRequest<unknown> {
  const currentUser = LocalStorageService.get('currentUser');
  // ``token`` is the historical name, still returned by login and all a
  // pre-rollout session has stored.
  const token = accessToken ?? currentUser?.access_token ?? currentUser?.token;

  if (!token) {
    return request;
  }

  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

function shouldAttemptRefresh(
  error: HttpErrorResponse,
  request: HttpRequest<unknown>,
  isApiRequest: boolean
): boolean {
  const currentUser = LocalStorageService.get('currentUser');

  return (
    error?.status === 401 &&
    isApiRequest &&
    // Pre-rollout sessions have no refresh token stored, so they cannot be
    // renewed and must fall through to logout.
    !!currentUser?.refresh_token &&
    !isAuthRequest(request.url)
  );
}

function isAuthRequest(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh/token') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/reset-password')
  );
}
